import {pick} from 'lodash-es';
import {useReducer, useState} from 'react';

import {referralService} from '@/services/referralService';
import type {AfriexReferralTier} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import updateTierReducer from './updateTierReducer';

// Deliberately partial: `bonusAmountPerReferrer` is required on
// `AfriexReferralTier` but is not seeded here, and `handleSubmit` sets it to 0
// on the way out. The parameter this feeds already takes a `Partial`.
const defaultInfo: Partial<AfriexReferralTier> = {
  name: '',
  bonusAmountPerReferree: 0,
  referralCode: '',
  isDeactivated: false,
  shouldAutomatePayout: true,
  shouldSkipReferrerPayout: true, // Always true for event tiers
  cumulativeThreshold: 10,
  transactionWindow: 30,
  type: 'event',
};

const useEventTierUpdate = (
  onClose: VoidFunction,
  initialData: Partial<AfriexReferralTier> = defaultInfo,
) => {
  const [tier, dispatch] = useReducer(
    updateTierReducer,
    initialData as AfriexReferralTier,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (rawTier: AfriexReferralTier) => {
    setIsLoading(true);
    try {
      if (!rawTier.name) {
        showErrorMessage(`Please provide the name of the referral tier`);
        setIsLoading(false);
        return;
      }
      const tier: AfriexReferralTier = {
        ...rawTier,
        type: 'event',
        shouldSkipReferrerPayout: true, // Always skip referrer for event tiers
        bonusAmountPerReferrer: 0, // No referrer payout for events
      };
      if (rawTier.id) {
        const fieldsToInclude = [
          'name',
          'bonusAmountPerReferree',
          'referralCode',
          'isDeactivated',
          'type',
          'shouldSkipReferrerPayout',
          'cumulativeThreshold',
          'transactionWindow',
          'shouldAutomatePayout',
        ];
        await referralService.updateTier(
          rawTier.id,
          pick(tier, fieldsToInclude),
        );
        showSuccessMessage(`Event tier updated successfully`);
      } else {
        await referralService.createTier(tier);
        showSuccessMessage(`Event tier saved successfully`);
      }

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      showErrorMessage(
        `Error saving event tier ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    isLoading,
    tier,
  };
};

export default useEventTierUpdate;
